import { configureStore } from "@reduxjs/toolkit";
import AppReducer from "./store/appSlice";
import analyticsReducer from "../analytics/store/analyticsSlice";
import { AnnotationReducer } from "../annotation";
import { ConfigMapReducer } from "../configMap";
import { dataRefiningReducer } from "../common/dataRefining/";
import { DocumentReducer } from "../document";
import { documentSetsReducer } from "../documentSet";
import { userReducer } from "../user";
import { documentTypesReducer } from "../documentTypes";
import { userManagementReducer } from "../userManagement";
import { metadataReducer } from "../metadata";
import emailTemplatesReducer from "../emailTemplates/store/emailTemplatesSlice";
import { tourReducer } from "../tours";
import { annotatorReducer } from "../annotator";
import organizationReducer from "../organization/store/organizationSlice";
import { annotationTableControlsReducer } from "../annotationTableControls";
import Sentry from "../sentry";
import { userManagementApi } from "../userManagement/store/UserManagementApi";
import { emailTemplatesApi } from "../emailTemplates/store/emailTemplatesApi";
import { analyticsApi } from "../analytics/store/analyticsApi";
import { appApi } from "./store/appApi";
import { documentSetApi } from "../documentSet/store/documentSetApi";
import { documentApi } from "../document/store/documentApi";
import { configMapApi } from "../configMap/store/configMapApi";
import { documentTypeApi } from "../documentTypes/store/documentTypeApi";
import { annotationSideControlsApi } from "../annotationSideControls/store/annotationSideControlsApi";
import { userApi } from "../user/store/userApi";
import { organizationApi } from "../organization/store/organizationApi";
import { annotationSideControlsReducer } from "../annotationSideControls";
import { metadataApi } from "../metadata/store/metadataApi";
import { authProviderReducer } from "../authProvider";
import { externalDataApi } from "../masterData/store/externalDataApi";
import masterDataReducer from "../masterData/store/masterDataSlice";
import { configMapEditorApi } from "../configMapEditor/store/configMapEditorApi";
import { entityTypeApi } from "../configMapEditor/store/entityTypeApi";
import { categorizationCrudApi } from "../configMapEditor/store/categorizationApi";
import configMapEditorNewReducer from "../configMapEditor/store/configMapEditorSlice";
import configMapDraftReducer from "../configMapEditor/store/configMapDraftSlice";
import configMapListDraftReducer from "../configMapEditor/store/configMapListDraftSlice";
import { qualityLabApi } from "../qualityLab/store/qualityLabApi";
import { holdReasonsApi } from "../holdReasons/store/holdReasonsApi";

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  stateTransformer: (state) => {
    return {
      ...state,
      giganticState: null,
    };
  },
});

export const store = configureStore({
  reducer: {
    analyticsReducer: analyticsReducer,
    annotationReducer: AnnotationReducer,
    annotationSideControlsReducer: annotationSideControlsReducer,
    annotationTableControlsReducer: annotationTableControlsReducer,
    annotatorReducer: annotatorReducer,
    appReducer: AppReducer,
    authProviderReducer: authProviderReducer,
    configMapReducer: ConfigMapReducer,
    dataRefiningReducer: dataRefiningReducer,
    documentReducer: DocumentReducer,
    documentSetsReducer: documentSetsReducer,
    documentTypesReducer: documentTypesReducer,
    emailTemplatesReducer: emailTemplatesReducer,
    metadataReducer: metadataReducer,
    organizationReducer: organizationReducer,
    tourReducer: tourReducer,
    userManagementReducer: userManagementReducer,
    userReducer: userReducer,
    masterDataReducer: masterDataReducer,
    configMapEditorNewReducer: configMapEditorNewReducer,
    configMapDraftReducer: configMapDraftReducer,
    configMapListDraftReducer: configMapListDraftReducer,
    [analyticsApi.reducerPath]: analyticsApi.reducer,
    [annotationSideControlsApi.reducerPath]: annotationSideControlsApi.reducer,
    [appApi.reducerPath]: appApi.reducer,
    [configMapApi.reducerPath]: configMapApi.reducer,
    [documentApi.reducerPath]: documentApi.reducer,
    [documentSetApi.reducerPath]: documentSetApi.reducer,
    [documentTypeApi.reducerPath]: documentTypeApi.reducer,
    [emailTemplatesApi.reducerPath]: emailTemplatesApi.reducer,
    [metadataApi.reducerPath]: metadataApi.reducer,
    [organizationApi.reducerPath]: organizationApi.reducer,
    [userManagementApi.reducerPath]: userManagementApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [externalDataApi.reducerPath]: externalDataApi.reducer,
    [configMapEditorApi.reducerPath]: configMapEditorApi.reducer,
    [entityTypeApi.reducerPath]: entityTypeApi.reducer,
    [categorizationCrudApi.reducerPath]: categorizationCrudApi.reducer,
    [qualityLabApi.reducerPath]: qualityLabApi.reducer,
    [holdReasonsApi.reducerPath]: holdReasonsApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false })
      .concat(analyticsApi.middleware)
      .concat(appApi.middleware)
      .concat(annotationSideControlsApi.middleware)
      .concat(configMapApi.middleware)
      .concat(documentApi.middleware)
      .concat(documentSetApi.middleware)
      .concat(documentTypeApi.middleware)
      .concat(organizationApi.middleware)
      .concat(userApi.middleware)
      .concat(emailTemplatesApi.middleware)
      .concat(userManagementApi.middleware)
      .concat(metadataApi.middleware)
      .concat(externalDataApi.middleware)
      .concat(configMapEditorApi.middleware)
      .concat(entityTypeApi.middleware)
      .concat(categorizationCrudApi.middleware)
      .concat(holdReasonsApi.middleware)
      .concat(qualityLabApi.middleware),
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat(sentryReduxEnhancer),
  devTools: { trace: false },
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
